import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "@/hooks/use-toast"
import { api } from "@/lib/api-client"

// Types
export type DifficultyLevel = "beginner" | "intermediate" | "advanced"

export interface User {
  _id: string
  name: string
  surname: string
  email: string
  phone: string
  status: string
  gym: string[]
  userType: string
  expires: string
  createdAt: string
  updatedAt: string
  dietPlans?: string[]
  exercisePlans?: string[]
}

export interface Exercise {
  name: string
  sets: number
  reps: string
  rest: string
  description: string
}

export interface ExercisePlan {
  _id: string // Changed from id to _id
  name: string
  type: string
  difficulty: DifficultyLevel
  duration: number
  targetMuscles: string[]
  description: string
  equipment: string[]
  exercises: Exercise[]
  users?: User[] // Added users field
}

// API functions
export const useExercisePlans = () => {
  return useQuery({
    queryKey: ["exercisePlans"],
    queryFn: async () => {
      try {
        const response = await api.get("/exerciseplan")
        return response.data
      } catch (error) {
        console.error("Error fetching exercise plans:", error)
        throw error
      }
    },
  })
}

export const useExercisePlan = (id: string, options = {}) => {
  return useQuery({
    queryKey: ["exercisePlans", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("No plan ID provided")
        const response = await api.get(`/exerciseplan/${id}`)
        return response.data
      } catch (error) {
        console.error(`Error fetching exercise plan ${id}:`, error)
        throw error
      }
    },
    ...options,
    enabled: !!id && (options as any).enabled !== false,
  })
}

export const useCreateExercisePlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Omit<ExercisePlan, "_id" | "users">) => {
      try {
        const response = await api.post("/exerciseplan", data)
        return response.data
      } catch (error) {
        console.error("Error creating exercise plan:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercisePlans"] })
      toast({
        title: "Success",
        description: "Exercise plan created successfully",
      })
    },
    onError: (error) => {
      console.error("Error creating exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to create exercise plan",
        variant: "destructive",
      })
    },
  })
}

export const useUpdateExercisePlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Omit<ExercisePlan, "users">) => {
      try {
        // Use _id instead of id
        const response = await api.patch(`/exerciseplan/${data._id}`, data)
        return response.data
      } catch (error) {
        console.error("Error updating exercise plan:", error)
        throw error
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exercisePlans"] })
      queryClient.invalidateQueries({ queryKey: ["exercisePlans", variables._id] }) // Use _id instead of id
      toast({
        title: "Success",
        description: "Exercise plan updated successfully",
      })
    },
    onError: (error) => {
      console.error("Error updating exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to update exercise plan",
        variant: "destructive",
      })
    },
  })
}

export const useDeleteExercisePlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const response = await api.delete(`/exerciseplan/${id}`)
        return response.data
      } catch (error) {
        console.error("Error deleting exercise plan:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercisePlans"] })
      toast({
        title: "Success",
        description: "Exercise plan deleted successfully",
      })
    },
    onError: (error) => {
      console.error("Error deleting exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to delete exercise plan",
        variant: "destructive",
      })
    },
  })
}

// Assign exercise plan to user
export function useAssignExercisePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { userId: string; planId: string }) => {
      try {
        // Use the correct endpoint format: /exerciseplan/assign/:userId/:exerciseplanId
        const response = await api.post(`/exerciseplan/assign/${data.userId}/${data.planId}`)
        return response.data
      } catch (error) {
        console.error("Error assigning exercise plan:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["planAssignments"] })
      queryClient.invalidateQueries({ queryKey: ["userExercisePlans"] })
      queryClient.invalidateQueries({ queryKey: ["exercisePlans"] })
      toast({
        title: "Success",
        description: "Exercise plan assigned successfully",
      })
    },
    onError: (error) => {
      console.error("Error assigning exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to assign exercise plan",
        variant: "destructive",
      })
    },
  })
}

// Remove user from exercise plan (placeholder - will be updated when endpoint is provided)
export function useRemoveUserFromExercisePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { planId: string; userId: string }) => {
      try {
        // This will be updated when the endpoint is provided
        const response = await api.delete(`/exerciseplan/assign/${data.userId}/${data.planId}`)
        return response.data
      } catch (error) {
        console.error("Error removing user from exercise plan:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercisePlans"] })
      queryClient.invalidateQueries({ queryKey: ["userExercisePlans"] })
      toast({
        title: "Success",
        description: "User removed from exercise plan successfully",
      })
    },
    onError: (error) => {
      console.error("Error removing user from exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to remove user from exercise plan",
        variant: "destructive",
      })
    },
  })
}

// Generate exercise plan with AI
export function useGenerateExercisePlanWithAI() {
  return useMutation({
    mutationFn: async (data: { prompt: string }) => {
      try {
        // Set longer timeout for AI generation (60 seconds)
        const response = await api.post("/exerciseplan/generate-with-ai", data, {
          timeout: 60000, // 60 seconds
        })
        return response.data
      } catch (error) {
        console.error("Error generating exercise plan with AI:", error)
        throw error
      }
    },
    onError: (error) => {
      console.error("Error generating exercise plan with AI:", error)
      toast({
        title: "Error",
        description: "Failed to generate exercise plan with AI. This may take longer than usual - please try again.",
        variant: "destructive",
      })
    },
  })
}
